/**
 * Persistence of a half-filled competition application form in localStorage, so
 * that the entered values survive e.g. a language switch (which is a different
 * URL). Only ONE draft is kept at a time, keyed by competition id:
 *  - restored only when it belongs to the competition currently being viewed
 *    (same competition, any locale) and is not stale,
 *  - overwritten as soon as the user edits a form for another competition,
 *  - cleared after a successful submit,
 * so a leftover draft can never bleed into a different application.
 */
export interface ApplicationDraft {
	competitionId: string;
	updatedAt: number;
	fields: Record<string, string>;
	dogs: string[];
}

const KEY = "flyfetch:application-draft";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function storageAvailable(): boolean {
	try {
		return typeof localStorage !== "undefined";
	} catch {
		// Accessing localStorage can throw (e.g. disabled cookies / privacy mode).
		return false;
	}
}

export function loadApplicationDraft(competitionId: string): ApplicationDraft | null {
	if (!storageAvailable()) {
		return null;
	}
	try {
		const raw = localStorage.getItem(KEY);
		if (!raw) {
			return null;
		}
		const draft = JSON.parse(raw) as ApplicationDraft;
		if (draft.competitionId !== competitionId || !Array.isArray(draft.dogs) || typeof draft.fields !== "object") {
			return null;
		}
		if (typeof draft.updatedAt !== "number" || Date.now() - draft.updatedAt > MAX_AGE_MS) {
			localStorage.removeItem(KEY);
			return null;
		}
		return draft;
	} catch {
		return null;
	}
}

export function saveApplicationDraft(
	competitionId: string,
	fields: Record<string, string | undefined>,
	dogs: string[],
): void {
	if (!storageAvailable()) {
		return;
	}
	try {
		const cleaned: Record<string, string> = {};
		for (const [name, value] of Object.entries(fields)) {
			if (typeof value === "string" && value !== "") {
				cleaned[name] = value;
			}
		}
		// Nothing worth keeping → drop any previous draft instead of storing an empty one.
		if (Object.keys(cleaned).length === 0) {
			localStorage.removeItem(KEY);
			return;
		}
		const draft: ApplicationDraft = { competitionId, updatedAt: Date.now(), fields: cleaned, dogs };
		localStorage.setItem(KEY, JSON.stringify(draft));
	} catch {
		// Ignore quota / serialization errors — persistence is best-effort.
	}
}

export function clearApplicationDraft(): void {
	if (!storageAvailable()) {
		return;
	}
	try {
		localStorage.removeItem(KEY);
	} catch {
		// Ignore.
	}
}
